import { Play, Pause, RotateCcw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';

interface ReplayControlsProps {
  isPlaying: boolean;
  onPlayPause: () => void;
  onReset: () => void;
  speed: number;
  onSpeedChange: (speed: number) => void;
  currentIndex: number;
  totalPositions: number;
  currentPosition: {
    fix_time: string;
    speed_kmh: number | null;
    ignition: boolean | null;
  };
  onClose: () => void;
}

export function ReplayControls({
  isPlaying,
  onPlayPause,
  onReset,
  speed,
  onSpeedChange,
  currentIndex,
  totalPositions,
  currentPosition,
  onClose,
}: ReplayControlsProps) {
  const progress = ((currentIndex + 1) / totalPositions) * 100;

  return (
    <Card className="fixed top-24 left-1/2 -translate-x-1/2 z-30 bg-background/95 backdrop-blur-md shadow-2xl border-2">
      <div className="p-4 space-y-3">
        {/* Header com Close */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {/* Play/Pause e Reset */}
            <Button
              size="sm"
              variant={isPlaying ? 'secondary' : 'default'}
              onClick={onPlayPause}
              className="h-9 w-9 p-0"
            >
              {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </Button>
            
            <Button
              size="sm"
              variant="outline"
              onClick={onReset}
              className="h-9 w-9 p-0"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>

            {/* Speed Controls */}
            <div className="flex gap-1">
              {[1, 2, 5, 10].map(s => (
                <Button
                  key={s}
                  size="sm"
                  variant={speed === s ? 'default' : 'outline'}
                  onClick={() => onSpeedChange(s)}
                  className="h-9 w-10 p-0 text-xs"
                >
                  {s}x
                </Button>
              ))}
            </div>
          </div>

          {/* Close Button */}
          <Button
            size="sm"
            variant="ghost"
            onClick={onClose}
            className="h-9 w-9 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Info Line */}
        <div className="flex items-center gap-3 text-sm">
          <span className="text-muted-foreground">
            Posição {currentIndex + 1}/{totalPositions}
          </span>
          <span className="text-muted-foreground">•</span>
          <span className="text-muted-foreground">
            {new Date(currentPosition.fix_time).toLocaleTimeString('pt-BR')}
          </span>
          <span className="text-muted-foreground">•</span>
          <Badge variant="outline" className="text-xs">
            {currentPosition.speed_kmh?.toFixed(1) || '0.0'} km/h
          </Badge>
          <span className="text-muted-foreground">•</span>
          <Badge 
            variant={currentPosition.ignition ? 'default' : 'secondary'}
            className="text-xs"
          >
            {currentPosition.ignition ? '🔑 Ligada' : '🔒 Desligada'}
          </Badge>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
          <div 
            className="bg-primary h-full rounded-full transition-all duration-200"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </Card>
  );
}
